/**
 * useDataTextValue Hook
 *
 * Reusable hook for data-binding value extraction and formatting.
 * Extracts data-fetching logic from SharedDataTextComponent for reuse
 * by DataCard and other components needing data-bound values.
 *
 * Created: January 2026 (Phase 8: DataCard Integration with DataText)
 */

import { useMemo } from 'react';
import { ChartDataSource } from '@/types/chartDataSource';
import { useQueryTemplateData, isQueryTemplateDataSource, isWorkflowDataSource } from '@/hooks/useQueryTemplateData';
import { formatMetricValue } from '@/utils/PropertyFormatters';
import { fullyDecodeHtmlEntities } from '@/utils/htmlEntities';
import { isInertHomeAssistantPlaceholderBinding } from '@/utils/homeAssistantPlaceholder';

/**
 * Configuration options for useDataTextValue hook
 */
export interface UseDataTextValueOptions {
  /** Data source configuration for fetching value */
  dataSource?: ChartDataSource;
  /** Component ID for logging/caching */
  componentId: string;
  /** Field name to extract value from data source (default: 'value') */
  valueField?: string;
  /** Field name to extract label from data source */
  labelField?: string;
  /** Static value to use when data binding is disabled */
  staticValue?: string | number;
  /** Static label to use when data binding is disabled or labelField not set */
  staticLabel?: string;
  /** Text to display when value is null/undefined (default: '--') */
  emptyValueText?: string;
  /** Value formatter type (default: 'number') */
  valueFormatter?: string;
  /** Optional IANA timezone override for date/time formatting */
  timeZone?: string;
  /** Decimal places for numeric formatting */
  decimalPlaces?: number;
  /** Whether grouping separators should be shown */
  thousandsSeparator?: boolean;
  /** Currency symbol for currency formatting */
  currencySymbol?: string;
  /** Intl currency code for currency formatting */
  currencyCode?: string;
  /** Whether data binding is enabled (default: true when dataSource is provided) */
  enabled?: boolean;
  /** Direct template execution mode — uses queryTemplateApi.execute() instead of AI pipeline */
  templateId?: string;
}

/**
 * Result returned by useDataTextValue hook
 */
export interface UseDataTextValueResult {
  /** Raw extracted value (before formatting) */
  value: string | number | null;
  /** Formatted value string */
  formattedValue: string;
  /** Extracted or static label */
  label: string;
  /** Whether data is currently loading */
  isLoading: boolean;
  /** Error message if data fetch failed */
  error: string | null;
  /** Function to manually refetch data */
  refetch: () => Promise<void>;
  /** Whether a data source is configured and active */
  hasDataSource: boolean;
  /** Timestamp of last successful fetch */
  lastFetched: Date | null;
}

/**
 * Hook for data-binding value extraction and formatting.
 *
 * Encapsulates the data fetching and value extraction logic used by
 * DataText and DataCard components. Returns formatted values, loading
 * states, and error handling.
 *
 * @example
 * ```tsx
 * // In DataCard component
 * const {
 *   formattedValue,
 *   label,
 *   isLoading,
 *   error,
 * } = useDataTextValue({
 *   dataSource: props.valueDataSource,
 *   componentId: component.id,
 *   valueField: props.valueField,
 *   staticValue: props.value,
 *   staticLabel: props.label,
 *   valueFormatter: props.valueFormatter,
 *   enabled: props.useDataText === true,
 * });
 *
 * // Use formattedValue when useDataText is true
 * const displayValue = props.useDataText ? formattedValue : staticFormattedValue;
 * ```
 */
export function useDataTextValue({
  dataSource,
  componentId,
  valueField = 'value',
  labelField,
  staticValue,
  staticLabel = '',
  emptyValueText = '--',
  valueFormatter = 'number',
  timeZone,
  decimalPlaces,
  thousandsSeparator,
  currencySymbol,
  currencyCode,
  enabled = true,
  templateId,
}: UseDataTextValueOptions): UseDataTextValueResult {
  // Check if we have a valid data source (query template or workflow)
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);
  const hasWorkflow = isWorkflowDataSource(dataSource);
  const shouldFetch = enabled && (hasQueryTemplate || hasWorkflow || !!templateId);
  const isInertHomeAssistantPlaceholder = isInertHomeAssistantPlaceholderBinding(
    dataSource,
    templateId,
  );

  // Trace: log what useDataTextValue sees (helps debug "component shows --")
  if (typeof window !== 'undefined' && componentId?.includes('datacard-depth')) {
    // Temporary targeted trace for debugging
    import('@/services/tracing/TraceClient').then(({ traceClient }) => {
      if (traceClient.isEnabled()) {
        traceClient.createThread('data_source', `useDataTextValue:${componentId}`).then(thread => {
          if (thread) {
            traceClient.startSpan(thread.threadId, 'ui_data_request', `useDataTextValue eval: ${componentId}`, {
              detail: {
                status: shouldFetch ? 'will_fetch' : 'skipped',
                enabled,
                hasQueryTemplate,
                hasWorkflow,
                hasTemplateId: !!templateId,
                shouldFetch,
                dataSourceType: dataSource?.type,
                dataSourceExists: !!dataSource,
                workflowConfigExists: !!dataSource?.workflowConfig,
                workflowId: dataSource?.workflowConfig?.workflowId,
              },
            }).then(spanId => {
              if (spanId) traceClient.endSpan(spanId, shouldFetch ? 'ok' : 'error', {
                summary: shouldFetch ? 'Will fetch data' : `SKIPPED: enabled=${enabled}, hasWorkflow=${hasWorkflow}`,
              });
            });
          }
        }).catch(() => {});
      }
    }).catch(() => {});
  }

  // Fetch data using the existing hook
  const {
    data: queryData,
    isLoading,
    error,
    refetch,
    lastFetched,
  } = useQueryTemplateData({
    dataSource,
    componentId,
    enabled: shouldFetch,
    templateId,
  });

  // VALUE EXTRACTION
  const extractedValue = useMemo<string | number | null>(() => {
    if (shouldFetch && queryData && queryData.length > 0) {
      const row = queryData[0];
      // Try the specified valueField first, then fall back to:
      // - last segment of a dotted path (e.g. 'data.pressure' → 'pressure')
      // - nested property access along the dotted path
      // - "value" (standard pipeline projection field)
      let val = row[valueField];
      if (val === undefined && valueField.includes('.')) {
        const fieldPath = valueField.split('.');
        const shortKey = fieldPath[fieldPath.length - 1];
        val = shortKey ? row[shortKey] : undefined;
        if (val === undefined) {
          // Walk the dotted path for nested objects
          val = fieldPath.reduce<unknown>((obj, key) =>
            obj && typeof obj === 'object' ? (obj as Record<string, unknown>)[key] : undefined,
            row
          ) as string | number | undefined;
        }
      }
      if ((val === undefined || val === null) && valueField !== 'value') {
        val = row['value'];
      }
      // Smart fallback: if no match yet, use first numeric non-_id field (common for aggregations like {_id: null, avg_pressure: 69.3})
      if (val === undefined || val === null) {
        const candidate = Object.entries(row).find(
          ([k, v]) => k !== '_id' && (typeof v === 'number' || (typeof v === 'string' && v.length > 0))
        );
        if (candidate) val = candidate[1] as string | number;
      }
      if (val !== undefined && val !== null) {
        if (typeof val === 'string') return fullyDecodeHtmlEntities(val);
        return typeof val === 'number' ? val : String(val);
      }
      return null;
    }
    // Intentional HA placeholders keep their curated preview value even when
    // data binding metadata is present but no live query data is fetched.
    if (shouldFetch && !isInertHomeAssistantPlaceholder) {
      return null;
    }
    return staticValue ?? null;
  }, [
    shouldFetch,
    queryData,
    valueField,
    staticValue,
    isInertHomeAssistantPlaceholder,
  ]);

  // LABEL EXTRACTION
  const extractedLabel = useMemo<string>(() => {
    if (shouldFetch && queryData && queryData.length > 0 && labelField) {
      const labelVal = queryData[0][labelField];
      if (labelVal !== undefined && labelVal !== null) {
        return fullyDecodeHtmlEntities(String(labelVal));
      }
    }
    return staticLabel;
  }, [shouldFetch, queryData, labelField, staticLabel]);

  // VALUE FORMATTING
  const formattedValue = useMemo<string>(() => {
    if (extractedValue === null) {
      return emptyValueText;
    }

    // Handle both number and string values
    if (typeof extractedValue === 'string') {
      // Try to parse as number for formatting
      const numericValue = !isNaN(Number(extractedValue)) ? Number(extractedValue) : extractedValue;
      return formatMetricValue(numericValue, valueFormatter, {
        timeZone,
        decimalPlaces,
        thousandsSeparator,
        currencySymbol,
        currencyCode,
      });
    }

    return formatMetricValue(extractedValue, valueFormatter, {
      timeZone,
      decimalPlaces,
      thousandsSeparator,
      currencySymbol,
      currencyCode,
    });
  }, [
    extractedValue,
    valueFormatter,
    emptyValueText,
    timeZone,
    decimalPlaces,
    thousandsSeparator,
    currencySymbol,
    currencyCode,
  ]);

  return {
    value: extractedValue,
    formattedValue,
    label: extractedLabel,
    isLoading,
    error,
    refetch,
    hasDataSource: shouldFetch,
    lastFetched,
  };
}

export default useDataTextValue;
